/**
 * Sizes for collision queries that run in a splat's FILE (local) space.
 *
 * Voxel collision and the splat occupancy grid are stored in the splat's own coordinates, so a
 * query has to move from world space into file space and back. Positions go through the splat
 * entity's inverse world matrix, but SIZES — the walker's radius, the fly camera's radius, a
 * sweep's probe radius — must be converted too. If they are not, a splat scaled by s silently
 * scales the player by s: a walker on a ×2 splat collides as if it were twice as wide.
 *
 * Product rule: scaling the world never scales the player. Player width, height and step height
 * are world-space metres at any splat scale.
 *
 * Matrices are PlayCanvas Mat4 `data` arrays (column-major: element (row r, col c) = m[c * 4 + r]).
 * Pure math — no PlayCanvas import — so the conversions are unit-testable on their own.
 */

type Vec3Like = { x: number; y: number; z: number };

/**
 * File (local) units per world unit, conservatively: the largest stretch of the inverse splat
 * matrix, i.e. the largest row norm of its 3×3 part (= 1 / the smallest axis scale of the splat).
 * A world-space radius times this is a file-space radius whose sphere contains the world sphere's
 * image on every axis, so a non-uniformly scaled splat can only over-detect (the push-out
 * conversion below trims that back), never miss a contact.
 */
export function fileUnitsPerWorldUnit(invSplatWorld: ArrayLike<number>): number {
    const m = invSplatWorld;
    const k = Math.max(
        Math.hypot(m[0], m[4], m[8]),
        Math.hypot(m[1], m[5], m[9]),
        Math.hypot(m[2], m[6], m[10]),
    );
    return Number.isFinite(k) && k > 0 ? k : 1;
}

/**
 * Convert a voxel push-out from file space back to a world-space displacement for a sphere of
 * world radius `worldRadius` that was queried with file radius `fileRadius`.
 *
 * VoxelCollision pushes along file axes, so each axis component of the push is one face contact:
 * the sphere centre was `fileRadius - |push_i|` (file units) in front of a face whose normal is
 * file axis i. That face is a plane in world space too; its world normal is row i of the inverse
 * matrix (normalised) and the centre's world distance to it is the file distance divided by that
 * row's norm. The world push for the contact is then whatever moves the centre to `worldRadius`
 * from the plane — zero when the inflated file sphere touched a face the world sphere does not.
 *
 * For a uniformly scaled (and rotated / mirrored) splat this is exactly the splat matrix applied
 * to the push, i.e. the push-out at the correct world size; for a non-uniform scale it also
 * removes the over-detection from {@link fileUnitsPerWorldUnit}, so the player keeps its world
 * width along every axis.
 */
export function filePushOutToWorld(
    push: Vec3Like,
    fileRadius: number,
    worldRadius: number,
    invSplatWorld: ArrayLike<number>,
    out: Vec3Like,
): Vec3Like {
    const inv = invSplatWorld;
    let ox = 0, oy = 0, oz = 0;
    const comps = [push.x, push.y, push.z];
    for (let i = 0; i < 3; i++) {
        const p = comps[i];
        if (!(Math.abs(p) > 1e-12)) continue;
        // World normal of the file-axis-i face plane (unnormalised): row i of the inverse matrix.
        const nx = inv[i], ny = inv[4 + i], nz = inv[8 + i];
        const len = Math.hypot(nx, ny, nz);
        if (!(len > 1e-12) || !Number.isFinite(len)) continue;
        const worldDist = (fileRadius - Math.abs(p)) / len;
        const depth = worldRadius - worldDist;
        if (!(depth > 0)) continue;
        const k = Math.sign(p) * depth / len;
        ox += nx * k; oy += ny * k; oz += nz * k;
    }
    out.x = ox; out.y = oy; out.z = oz;
    return out;
}
